import { RouteButtonLink } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  AppBar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  OutlinedInput,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { Proposal } from "../../../../../@types/services/Proposal";
import type { PurchaseRequisition } from "../../../../../@types/services/PurchaseRequisition";
import { showServiceNegotiation } from "../../../../../__generated__/orval/endpoints/service-negotiations/service-negotiations";
import type {
  EntitiesServiceNegotiationDetailOutput,
  EntitiesServiceNegotiationServiceLineOutput,
} from "../../../../../__generated__/orval/models";
import { Chat } from "../../../../../components/molecules/Chat";
import { DownloadProposals } from "../../../../../components/molecules/DownloadProposals";
import { ListAttachmentServiceNegotiation } from "../../../../../components/molecules/ListAttachmentServiceNegotiation";
import { ProposalAudit } from "../../../../../components/molecules/ProposalAudit";
import { RejectServiceNegotiation } from "../../../../../components/molecules/RejectServiceNegotiation";
import { RevertRejection } from "../../../../../components/molecules/RevertRejection";
import { ServiceBillingInformation } from "../../../../../components/molecules/ServiceBillingInformation";
import { ServiceQuotationOfferDeadline } from "../../../../../components/molecules/ServiceQuotationOfferDeadline";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { useAuth } from "../../../../../hooks/useAuth";
import { fetchProposal } from "../../../../../services/proposals";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";
import {
  canEditProposal as canEditProposalByRules,
  PROPOSAL_EDIT_RENEGOTIATION_STATUS_NAMES,
} from "../-internal_components/proposalEditRules";

const PROPOSAL_CREATION_STATUS_NAMES = ["Waiting Offer"];
const TAB_VALUES = {
  attachments: "2",
  audits: "4",
  chat: "1",
  moreInformation: "3",
  proposal: "0",
} as const;

const searchSchema = z.object({
  tab: z.optional(z.enum(["chat"])).catch(undefined),
});

type ServiceNegotiationWithRenegotiationState =
  EntitiesServiceNegotiationDetailOutput & {
    updated_in_current_renegotiation_round?: boolean;
  };

const ShowServiceNegotiation: React.FC = () => {
  const serviceNegotiation = Route.useLoaderData();
  const { chat_notification_id: chatNotificationId, tab } = Route.useSearch();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isVendorUser = !!user && !user.equinor;
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const [scrollToLatestRequest, setScrollToLatestRequest] = useState(0);
  const focusedNotificationRequest = useRef(0);

  useEffect(() => {
    if (chatNotificationId) {
      setScrollToLatestRequest((request) => request + 1);
    }
  }, [chatNotificationId]);

  const [proposal, setProposal] = useState<Proposal>();
  const [value, setValue] = useState<string>(
    tab === "chat" ? TAB_VALUES.chat : TAB_VALUES.proposal,
  );
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [expandedDescriptionForVendor, setExpandedDescriptionForVendor] =
    useState(false);
  const [scopeOfWorkFocusTrigger, setScopeOfWorkFocusTrigger] = useState(0);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      setValue(newValue);
    },
    [],
  );

  const handleGoToScopeOfWork = useCallback(() => {
    setValue(TAB_VALUES.attachments);
    setScopeOfWorkFocusTrigger((prev) => prev + 1);
  }, []);

  const handleFetchProposal = useCallback(async (id: string | number) => {
    await fetchProposal(String(id)).then((resource) => {
      setProposal(() => resource);
    });
  }, []);

  const getStatusInfo = useCallback(
    (serviceNegotiationParam: EntitiesServiceNegotiationDetailOutput) => {
      const negotiationStatusName = serviceNegotiationParam.status?.name;
      const negotiationStatusTitle = serviceNegotiationParam.status?.title;
      const quotationStatusName =
        serviceNegotiationParam.service_quotation?.status?.name;
      const statusNameForBadge = negotiationStatusName ?? quotationStatusName;
      const normalizedStatusName =
        typeof statusNameForBadge === "string" ? statusNameForBadge : "";

      const isOfferWindowStatus = [
        "Waiting Send",
        "Waiting Offers",
        "Waiting Offer",
        "Waiting Bid Timeout",
      ].includes(normalizedStatusName);

      const isFinished =
        serviceNegotiationParam.process_instance?.completed ||
        negotiationStatusName === "Rejected Offer";
      const isCancelled = [
        "Cancelled",
        "Service Negotiation Cancelled",
      ].includes(negotiationStatusName || "");

      if (isFinished && isCancelled) {
        return {
          color: "error",
          text: t("serviceNegotiation.steps.cancelled"),
        };
      }

      if (isFinished) {
        return {
          color: "success",
          text: t("serviceNegotiation.steps.finished"),
        };
      }

      if (
        !serviceNegotiationParam.service_quotation?.purchase_order_item &&
        isOfferWindowStatus
      ) {
        return {
          color: "primary",
          text: t("serviceNegotiation.steps.waiting_offer"),
        };
      }

      if (negotiationStatusTitle || negotiationStatusName) {
        return {
          color: "primary",
          text: negotiationStatusTitle || negotiationStatusName,
        };
      }

      return {
        color: "primary",
        text: t("serviceNegotiation.steps.evaluating_proposals"),
      };
    },
    [t],
  );

  useEffect(() => {
    if (!chatNotificationId && tab !== "chat") {
      return;
    }

    setValue(TAB_VALUES.chat);
  }, [chatNotificationId, tab]);

  useEffect(() => {
    if (
      scrollToLatestRequest === focusedNotificationRequest.current ||
      value !== TAB_VALUES.chat ||
      !serviceNegotiation.chat ||
      !user
    ) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      if (!chatPanelRef.current) return;
      chatPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      chatPanelRef.current?.focus({ preventScroll: true });
      focusedNotificationRequest.current = scrollToLatestRequest;
    });

    return () => cancelAnimationFrame(frame);
  }, [scrollToLatestRequest, serviceNegotiation.chat, user, value]);

  useEffect(() => {
    if (serviceNegotiation.proposal) {
      handleFetchProposal(serviceNegotiation.proposal.id.toString());
    }
  }, [handleFetchProposal, serviceNegotiation]);

  const canCreateProposal = PROPOSAL_CREATION_STATUS_NAMES.includes(
    serviceNegotiation?.status?.name || "",
  );

  const isRenegotiationStatus =
    PROPOSAL_EDIT_RENEGOTIATION_STATUS_NAMES.includes(
      serviceNegotiation?.status?.name || "",
    );

  const proposalUpdatedInCurrentRenegotiation = Boolean(
    (serviceNegotiation as ServiceNegotiationWithRenegotiationState)
      .updated_in_current_renegotiation_round,
  );

  const canEditProposal = canEditProposalByRules(
    serviceNegotiation?.status?.name,
    serviceNegotiation?.service_quotation?.offer_submission_deadline,
  );

  const purchaseRequisitionForComponent = serviceNegotiation?.service_quotation
    ?.pr_item as unknown as PurchaseRequisition | undefined;

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <Container>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexGrow: 1,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ paddingY: 2 }}>
            <Box sx={{ alignItems: "center", display: "flex" }}>
              <Typography variant="h5">
                {t("serviceNegotiation.title")}
              </Typography>
              <Chip
                color={
                  getStatusInfo(serviceNegotiation).color as
                    | "success"
                    | "error"
                    | "primary"
                    | "info"
                    | "default"
                }
                label={getStatusInfo(serviceNegotiation).text}
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                  ml: 1,
                  textTransform: "uppercase",
                }}
              />
            </Box>
            <Typography variant="h4">
              #{serviceNegotiation?.service_quotation.identifier}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
              }}
              variant="h6"
            >
              {serviceNegotiation?.service_quotation.reference_number}
            </Typography>
          </Box>
        </Box>
        {serviceNegotiation?.proposal &&
          (!isRenegotiationStatus || proposalUpdatedInCurrentRenegotiation) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                proposalUpdatedInCurrentRenegotiation
                  ? "serviceNegotiation.proposalRenegotiatedNotice"
                  : "serviceNegotiation.proposalSentNotice",
              )}
            </Alert>
          )}
        <Box sx={{ flexGrow: 1 }}>
          <AppBar color="white" position="static">
            <Toolbar sx={{ position: "relative" }} variant="dense">
              <Typography component="div" variant="h6">
                {t("details")}
              </Typography>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  inset: 0,
                  justifyContent: "center",
                  pointerEvents: "none",
                  position: "absolute",
                }}
              >
                <ServiceQuotationOfferDeadline
                  offerSubmissionDeadline={
                    serviceNegotiation?.service_quotation
                      ?.offer_submission_deadline
                  }
                  showOfferPeriodEnded={isVendorUser}
                  statusName={serviceNegotiation?.status?.name}
                />
              </Box>
              <Box sx={{ alignItems: "center", display: "flex", ml: "auto" }}>
                {!!serviceNegotiation?.service_quotation?.purchase_order_item
                  ?.purchase_order?.purchase_order_pdf_document?.attachment && (
                  <Button
                    color="success"
                    disableElevation
                    download={
                      serviceNegotiation.service_quotation.purchase_order_item
                        .purchase_order.purchase_order_pdf_document.attachment
                        .filename
                    }
                    href={`/vbuyer/purchase_orders/${serviceNegotiation.service_quotation.purchase_order_item.purchase_order.id}/download.pdf`}
                    sx={{ marginRight: 2 }}
                    target="_blank"
                    variant="contained"
                  >
                    Download Purchase Order
                  </Button>
                )}
                {!serviceNegotiation?.proposal &&
                  serviceNegotiation?.status.name !== "Rejected By Vendor" && (
                    <Button
                      color="error"
                      onClick={() => {
                        setRejectOpen(true);
                      }}
                      sx={{ marginRight: 2 }}
                      variant="contained"
                    >
                      {t("reject")}
                    </Button>
                  )}
                {/* TODO: Replace the hardcoded colors in the code with theme variables. */}
                {serviceNegotiation?.status.name === "Rejected By Vendor" && (
                  <Button
                    color="info"
                    onClick={() => {
                      setRevertOpen(true);
                    }}
                    sx={{ marginRight: 2 }}
                    variant="contained"
                  >
                    {t("revertReject")}
                  </Button>
                )}
                <Button onClick={handleGoToScopeOfWork} variant="contained">
                  {t("serviceNegotiation.scopeOfWork")}
                </Button>
              </Box>
            </Toolbar>
          </AppBar>
        </Box>
        {!!serviceNegotiation?.id && (
          <RejectServiceNegotiation
            rejectOpen={rejectOpen}
            serviceNegotiationId={serviceNegotiation.id}
            setRejectOpen={setRejectOpen}
          />
        )}
        {!!serviceNegotiation?.id && (
          <RevertRejection
            revertOpen={revertOpen}
            serviceNegotiationId={serviceNegotiation.id}
            setRevertOpen={setRevertOpen}
          />
        )}

        <Card square variant="outlined">
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceNegotiation.prNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceNegotiation?.service_quotation?.pr_item?.pr_number ||
                    ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.itemNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceNegotiation?.service_quotation?.pr_item
                    ?.item_number || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.requisitionDate")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceNegotiation?.service_quotation?.pr_item
                    ?.requisition_date
                    ? t(
                        "intlDate",
                        i18nextParseToDate(
                          serviceNegotiation?.service_quotation.pr_item
                            .requisition_date,
                        ),
                      )
                    : ""}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceNegotiation.shortDescription")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceNegotiation?.service_quotation?.short_description ||
                    ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.plant")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceNegotiation?.service_quotation?.pr_item?.plant
                    ?.name || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.quantity")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceNegotiation?.service_quotation?.pr_item?.quantity ||
                    ""}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2">
                    {t("serviceNegotiation.description")}
                  </Typography>
                  <Button
                    onClick={() =>
                      setExpandedDescriptionForVendor(
                        !expandedDescriptionForVendor,
                      )
                    }
                    size="small"
                    variant="text"
                  >
                    {expandedDescriptionForVendor
                      ? t("serviceQuotation.collapseDescription")
                      : t("serviceQuotation.expandDescription")}
                  </Button>
                </Box>
                <OutlinedInput
                  disabled
                  fullWidth
                  multiline
                  placeholder={t("serviceNegotiation.description")}
                  rows={expandedDescriptionForVendor ? 15 : 5}
                  style={{
                    fontSize: "0.9em",
                    marginTop: "16px",
                  }}
                  value={
                    serviceNegotiation?.service_quotation
                      ?.description_for_vendor || ""
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
      <Container>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList aria-label="basic tabs example" onChange={handleChange}>
              <Tab label={t("proposal.proposal")} value={TAB_VALUES.proposal} />
              <Tab label={t("chat")} value={TAB_VALUES.chat} />
              <Tab
                label={t("common.attachments")}
                value={TAB_VALUES.attachments}
              />
              <Tab
                label={t("serviceQuotation.moreInformation")}
                value={TAB_VALUES.moreInformation}
              />
              {!isVendorUser && (
                <Tab
                  label={t("serviceNegotiation.audits")}
                  value={TAB_VALUES.audits}
                />
              )}
            </TabList>
          </Box>
          <TabPanel value={TAB_VALUES.proposal}>
            <Box sx={{ flexGrow: 1 }}>
              <AppBar color="white" position="static">
                <Toolbar variant="dense">
                  <Typography component="div" sx={{ flexGrow: 1 }} variant="h6">
                    {t("proposal.proposal")}
                  </Typography>
                  {!proposal && canCreateProposal && (
                    <Box
                      sx={{
                        background: "#FFFFFF",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <ButtonGroup
                        aria-label="Small button group"
                        size="small"
                        sx={{ "& > *": { mr: 1 } }}
                      >
                        <Button
                          color="primary"
                          component={Link}
                          to={`/vbuyer/service_negotiations/${serviceNegotiation.id}/proposals/new`}
                          variant="contained"
                        >
                          {t("serviceNegotiation.button.newProposal")}
                        </Button>
                      </ButtonGroup>
                    </Box>
                  )}
                  {/* TODO: Replace the hardcoded colors in the code with theme variables. */}
                  {!!proposal && canEditProposal && (
                    <Box
                      sx={{
                        background: "#FFFFFF",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <ButtonGroup
                        aria-label="Small button group"
                        size="small"
                        sx={{ "& > *": { mr: 1 } }}
                      >
                        {/* Edit Button */}
                        <RouteButtonLink
                          color="success"
                          params={{
                            proposalId: proposal.id,
                            serviceNegotiationId:
                              serviceNegotiation.id.toString(),
                          }}
                          to="/vbuyer/service_negotiations/$serviceNegotiationId/proposals/$proposalId/edit"
                          variant="contained"
                        >
                          {t("serviceNegotiation.button.editProposal")}
                        </RouteButtonLink>
                      </ButtonGroup>
                    </Box>
                  )}
                </Toolbar>
              </AppBar>
            </Box>
            <Container
              sx={{
                marginTop: "5px",
                minHeight: "400px",
                padding: "10px",
              }}
            >
              {!proposal && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    my: 2,
                  }}
                >
                  <Chip
                    label={<span>{t("proposal.createANewProposal")}</span>}
                  />
                </Box>
              )}
              {!!proposal && <DownloadProposals proposal={proposal} />}
              {!!proposal && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-evenly",
                      my: 2,
                    }}
                  >
                    <Chip
                      label={
                        proposal.lead_time != null
                          ? `${t("newProposal.leadTimeDays")}: ${proposal.lead_time}`
                          : `${t("newProposal.deliveryDate")}: ${
                              proposal.delivery_date
                                ? t(
                                    "intlDate",
                                    i18nextParseToDate(proposal.delivery_date),
                                  )
                                : "-"
                            }`
                      }
                    />
                    <Chip
                      label={`${t("newProposal.createdAt")}: ${t("intlDate", i18nextParseToDate(proposal.created_at))}`}
                    />
                    <Chip
                      label={`${t("newProposal.updatedAt")}: ${t("intlDate", i18nextParseToDate(proposal.updated_at))}`}
                    />
                  </Box>

                  <TableContainer component={Paper}>
                    <Table aria-label="simple table" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("serviceNegotiation.shortDescription")}
                          </TableCell>
                          <TableCell align="right">
                            {t("newProposal.quantity")}
                          </TableCell>
                          <TableCell align="right">
                            {t("newProposal.measureUnit")}
                          </TableCell>
                          <TableCell align="right">
                            {t("serviceNegotiation.grossPrice")}
                          </TableCell>
                          <TableCell align="right">
                            {t("common.total")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(
                          serviceNegotiation.service_quotation
                            .service_lines as unknown as
                            | EntitiesServiceNegotiationServiceLineOutput[]
                            | undefined
                        )?.map((sl) => {
                          const subItems = proposal.proposal_lines.filter(
                            (pl) =>
                              pl.service_line_id != null &&
                              String(pl.service_line_id) === String(sl.id),
                          );
                          return (
                            <React.Fragment key={String(sl.id)}>
                              {subItems
                                .sort((a, b) => a.position - b.position)
                                .map((pl) => (
                                  <TableRow key={pl.id}>
                                    <TableCell>{pl.short_text}</TableCell>
                                    <TableCell align="right">
                                      {pl.quantity}
                                    </TableCell>
                                    <TableCell align="right">
                                      {pl.measure_unit?.name}
                                    </TableCell>
                                    <TableCell align="right">
                                      {proposal.currency.unit} {pl.gross_price}
                                    </TableCell>
                                    <TableCell align="right">
                                      {proposal.currency.unit}{" "}
                                      {(
                                        (pl.gross_price ?? 0) *
                                        (pl.quantity ?? 0)
                                      ).toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </React.Fragment>
                          );
                        })}

                        {proposal.proposal_lines.some(
                          (pl) => pl.service_line_id == null,
                        ) && (
                          <>
                            <TableRow sx={{ bgcolor: "grey.200" }}>
                              <TableCell
                                colSpan={5}
                                sx={{ fontWeight: "bold", py: 1 }}
                              >
                                {t("newProposal.costBreakdown")}
                              </TableCell>
                            </TableRow>
                            {proposal.proposal_lines
                              .filter((pl) => pl.service_line_id == null)
                              .sort((a, b) => a.position - b.position)
                              .map((pl) => (
                                <TableRow key={pl.id}>
                                  <TableCell>{pl.short_text}</TableCell>
                                  <TableCell align="right">
                                    {pl.quantity}
                                  </TableCell>
                                  <TableCell align="right">
                                    {pl.measure_unit?.name}
                                  </TableCell>
                                  <TableCell align="right">
                                    {proposal.currency.unit} {pl.gross_price}
                                  </TableCell>
                                  <TableCell align="right">
                                    {proposal.currency.unit}{" "}
                                    {(
                                      (pl.gross_price ?? 0) * (pl.quantity ?? 0)
                                    ).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </>
                        )}

                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell colSpan={3} />
                          <TableCell align="right">
                            <strong>{t("newProposal.grandTotal")}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {proposal.currency.unit} {proposal.price}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Container>
          </TabPanel>
          <TabPanel value={TAB_VALUES.chat}>
            <Container
              ref={chatPanelRef}
              sx={{ marginTop: "5px", minHeight: "400px" }}
              tabIndex={-1}
            >
              <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                {!!serviceNegotiation.chat && !!user && (
                  <Chat
                    chatId={serviceNegotiation.chat.id}
                    currentUserId={user.id}
                    key={serviceNegotiation.chat.id}
                    scrollToLatestRequest={scrollToLatestRequest}
                    title={t("serviceNegotiation.chat")}
                  />
                )}
              </Box>
            </Container>
          </TabPanel>
          <TabPanel value={TAB_VALUES.attachments}>
            <Container sx={{ marginTop: "5px", minHeight: "450px" }}>
              {serviceNegotiation && (
                <ListAttachmentServiceNegotiation
                  focusScopeOfWorkTrigger={scopeOfWorkFocusTrigger}
                  serviceNegotiation={serviceNegotiation}
                />
              )}
            </Container>
          </TabPanel>
          <TabPanel value={TAB_VALUES.moreInformation}>
            <Container sx={{ marginTop: "5px", minHeight: "450px" }}>
              {!!purchaseRequisitionForComponent && (
                <ServiceBillingInformation
                  purchaseRequisition={
                    purchaseRequisitionForComponent as unknown as PurchaseRequisition
                  }
                />
              )}
            </Container>
          </TabPanel>
          {!isVendorUser && (
            <TabPanel value={TAB_VALUES.audits}>
              <Container sx={{ marginTop: "5px", minHeight: "450px" }}>
                {!!proposal && (
                  <ProposalAudit proposal={proposal as unknown as Proposal} />
                )}
              </Container>
            </TabPanel>
          )}
        </TabContext>
      </Container>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/service_negotiations/$serviceNegotiationId/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: ShowServiceNegotiation,
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Service Negotiation - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ params: { serviceNegotiationId } }) =>
    showServiceNegotiation(parseInt(serviceNegotiationId, 10)),
});
